import { useEffect, useMemo, useState } from 'react';
import { ISearchEpg } from '../../../interfaces/ISearchEpg.ts';
import DateHelper from '../../../helpers/dateHelper.ts';
import { useChannelsService } from '../../../hooks/useChannelsService.tsx';

import AccountFavoritePreviousNextItem from './widgets/AccountFavoritePreviousNextItem.tsx';
import AccountFavoriteCurrentItem from './widgets/AccountFavoriteCurrentItem.tsx';

interface IProps {
    favoriteDetails: ISearchEpg;
}

const tabs = [
    { name: 'Poprzednio', key: 'previous' as const },
    { name: 'Aktualnie', key: 'current' as const },
    { name: 'Następne', key: 'next' as const },
] as const;

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

const AccountFavoriteDetails = ({ favoriteDetails }: IProps) => {
    const channelService = useChannelsService();
    const [activeTab, setActiveTab] = useState<'previous' | 'current' | 'next'>('current');
    const [isVisible, setIsVisible] = useState(false);
    const [contentOpacity, setContentOpacity] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50);
        return () => {
            clearTimeout(timer);
        };
    }, []);

    const handleTabClick = (key: 'previous' | 'current' | 'next') => {
        if (activeTab === key) return;
        setContentOpacity(0);
        setTimeout(() => {
            setActiveTab(key);
            setContentOpacity(1);
        }, 150);
    };

    const channelMap = useMemo(() => {
        const map = new Map<number, any>();
        channelService.channels.forEach(channel => {
            map.set(channel.ch_id, channel);
        });
        return map;
    }, [channelService.channels]);

    const now = DateHelper.DateTimeNowUnixTimestamp();

    const previousEpg = useMemo(() => {
        return favoriteDetails.epgItems
            ?.filter(epg => epg.timeTo < now)
            .sort((a, b) => b.time - a.time) || [];
    }, [favoriteDetails.epgItems, now]);

    const currentEpg = useMemo(() => {
        return favoriteDetails.epgItems
            ?.filter(epg => epg.time < now && epg.timeTo > now) || [];
    }, [favoriteDetails.epgItems, now]);

    const nextEpg = useMemo(() => {
        return favoriteDetails.epgItems
            ?.filter(epg => epg.time > now) || [];
    }, [favoriteDetails.epgItems, now]);

    const renderContent = () => {
        switch (activeTab) {
            case 'previous':
                return previousEpg.length === 0 ? (
                    <div className="text-center py-8 text-neutral-300">Brak poprzednich transmisji</div>
                ) : (
                    previousEpg.map(epg => (
                        <AccountFavoritePreviousNextItem
                            key={epg.id}
                            item={epg}
                            channel={channelMap.get(epg.channelId)}
                        />
                    ))
                );

            case 'current':
                return currentEpg.length === 0 ? (
                    <div className="text-center py-8 text-neutral-300">Brak aktualnej transmisji</div>
                ) : (
                    currentEpg.map(epg => (
                        <AccountFavoriteCurrentItem
                            key={epg.id}
                            favorite={epg}
                            channel={channelMap.get(epg.channelId)}
                        />
                    ))
                );

            case 'next':
                return nextEpg.length === 0 ? (
                    <div className="text-center py-8 text-neutral-300">Brak następnych transmisji</div>
                ) : (
                    nextEpg.map(epg => (
                        <AccountFavoritePreviousNextItem
                            key={epg.id}
                            item={epg}
                            channel={channelMap.get(epg.channelId)}
                        />
                    ))
                );
        }
    };

    return (
        <div
            className={`relative h-full flex flex-col transition-opacity duration-300 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="border-b border-white/10">
                <nav aria-label="Tabs" className="-mb-px flex">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => handleTabClick(tab.key)}
                            className={classNames(
                                activeTab === tab.key
                                    ? 'border-[#008DD2] text-[#008DD2]'
                                    : 'border-transparent text-neutral-400 hover:border-white/20 hover:text-neutral-300',
                                'w-1/3 border-b-2 px-1 py-4 text-center text-sm font-medium transition',
                            )}
                        >
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="overflow-y-auto flex-1 transition-opacity duration-300 ease-in-out"
                 style={{ opacity: contentOpacity }}>
                {renderContent()}
            </div>
        </div>
    );
};

export default AccountFavoriteDetails;